// Score history + streak. Uses Supabase's REST API via plain fetch when the two
// env vars are set (no SDK dependency needed); otherwise falls back to
// localStorage so the app works before any backend exists.
//
// Supabase table expected (see DEPLOY.md for SQL):
//   articulation_sessions(id uuid, created_at timestamptz, played_on date,
//                         avg_score int, reps jsonb)

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const useSupabase = Boolean(SUPABASE_URL && SUPABASE_KEY);

const LS_KEY = "articulation_sessions";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function pad(n) {
  return String(n).padStart(2, "0");
}

function readLocal() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
  } catch {
    return [];
  }
}
function writeLocal(rows) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(rows));
  } catch {
    /* storage may be blocked; ignore */
  }
}

export async function saveSession({ avgScore, reps }) {
  const row = {
    created_at: new Date().toISOString(),
    played_on: todayISO(),
    avg_score: Math.round(avgScore),
    reps,
  };

  if (useSupabase) {
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/articulation_sessions`, {
        method: "POST",
        headers: {
          apikey: SUPABASE_KEY,
          Authorization: `Bearer ${SUPABASE_KEY}`,
          "Content-Type": "application/json",
          Prefer: "return=minimal",
        },
        body: JSON.stringify(row),
      });
    } catch (e) {
      // Network failed — keep a local copy so the streak still counts.
      const rows = readLocal();
      rows.push(row);
      writeLocal(rows);
    }
  } else {
    const rows = readLocal();
    rows.push(row);
    writeLocal(rows);
  }
}

export async function getHistory() {
  if (useSupabase) {
    try {
      const res = await fetch(
        `${SUPABASE_URL}/rest/v1/articulation_sessions?select=played_on,avg_score,created_at&order=created_at.desc&limit=200`,
        {
          headers: {
            apikey: SUPABASE_KEY,
            Authorization: `Bearer ${SUPABASE_KEY}`,
          },
        }
      );
      if (res.ok) return await res.json();
    } catch (e) {
      /* fall through to local */
    }
  }
  return readLocal().slice().reverse();
}

// Streak = consecutive calendar days (ending today or yesterday) with >=1 session.
export function computeStreak(history) {
  const days = new Set(history.map((r) => r.played_on));
  if (days.size === 0) return { current: 0, playedToday: false };

  const playedToday = days.has(todayISO());
  let cursor = new Date();
  // If today isn't done yet, a streak can still be "alive" from yesterday.
  if (!playedToday) cursor.setDate(cursor.getDate() - 1);

  let current = 0;
  while (true) {
    const iso = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}-${pad(
      cursor.getDate()
    )}`;
    if (days.has(iso)) {
      current += 1;
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }
  return { current, playedToday };
}

export function bestScore(history) {
  return history.reduce((m, r) => Math.max(m, r.avg_score || 0), 0);
}

export const backend = useSupabase ? "supabase" : "local";
